import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Eye, Search } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Card } from "../../components/ui/card";
import { ContentLayout } from "../../components/admin-panel/content-layout";
import Pagination from "../../components/Pagination";
import DeleteModal from "../../components/deleteModal";
import ModalPopUpCompany from "../../components/AddAdmin";
import SuperAdminService from "../../services/superAdmin";
import { toastError, toastSuccess } from "../../components/ToastNotification";
import { useNavigate } from "react-router-dom";
import Loader from "../../assets/images/loader.gif";
import {
  setAdminData,
  clearAdminCache,
  adminListCacheKey,
} from "../../store/slices/superAdmin";
import { Input } from "../../components/ui/input";

const Admin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    individualAdmins,
    organizationAdmins,
    superAdmins = {},
  } = useSelector((state) => state.superAdmin);

  const isLoggedin = localStorage.getItem("guideproaAI_admin");
  const [individualCurrentPage, setIndividualCurrentPage] = useState(1);
  const [organizationCurrentPage, setOrganizationCurrentPage] = useState(1);
  const [superAdminCurrentPage, setSuperAdminCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [userType, setUserType] = useState("IndividualAdmin");
  const [individualSearchInput, setIndividualSearchInput] = useState("");
  const [organizationSearchInput, setOrganizationSearchInput] = useState("");
  const [superAdminSearchInput, setSuperAdminSearchInput] = useState("");
  const [debouncedIndividualSearch, setDebouncedIndividualSearch] = useState("");
  const [debouncedOrganizationSearch, setDebouncedOrganizationSearch] = useState("");
  const [debouncedSuperAdminSearch, setDebouncedSuperAdminSearch] = useState("");
  const skipInitialIndividualSearchFetch = useRef(true);
  const skipInitialOrganizationSearchFetch = useRef(true);
  const skipInitialSuperAdminSearchFetch = useRef(true);

  const SEARCH_DEBOUNCE_MS = 400;

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedIndividualSearch(individualSearchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [individualSearchInput]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedOrganizationSearch(organizationSearchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [organizationSearchInput]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSuperAdminSearch(superAdminSearchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [superAdminSearchInput]);

  const individualListKey = adminListCacheKey(
    individualCurrentPage,
    debouncedIndividualSearch
  );
  const organizationListKey = adminListCacheKey(
    organizationCurrentPage,
    debouncedOrganizationSearch
  );
  const superAdminListKey = adminListCacheKey(
    superAdminCurrentPage,
    debouncedSuperAdminSearch
  );

  const currentIndividualData = individualAdmins[individualListKey]?.data || [];
  const currentOrganizationData = organizationAdmins[organizationListKey]?.data || [];
  const currentSuperAdminData = superAdmins[superAdminListKey]?.data || [];

  const individualTotalPages = individualAdmins[individualListKey]?.totalPages || 0;
  const organizationTotalPages = organizationAdmins[organizationListKey]?.totalPages || 0;
  const superAdminTotalPages = superAdmins[superAdminListKey]?.totalPages || 0;

  const tableColSpan =
    userType === "OrganizationAdmin" ? 6 : 5;

  const currentTableRows =
    userType === "IndividualAdmin"
      ? currentIndividualData
      : userType === "OrganizationAdmin"
        ? currentOrganizationData
        : currentSuperAdminData;


  console.log("🚀  currentTableRows:", currentTableRows)

  const currentPageForType =
    userType === "IndividualAdmin"
      ? individualCurrentPage
      : userType === "OrganizationAdmin"
        ? organizationCurrentPage
        : superAdminCurrentPage;

  const totalPagesForType =
    userType === "IndividualAdmin"
      ? individualTotalPages
      : userType === "OrganizationAdmin"
        ? organizationTotalPages
        : superAdminTotalPages;

  // Note: Since totalPages is likely the same for all pages in the cache, 
  // we can just take it from any stored page, or store it separately in Redux.
  // For now, let's keep it simple.

  // Function to handle page change
  const handlePageChange = (page, event) => {
    event.preventDefault();
    if (userType === "IndividualAdmin") {
      if (page >= 1 && page <= individualTotalPages) {
        setIndividualCurrentPage(page);
        getAllAdmin("IndividualAdmin", page);
      }
    } else if (userType === "OrganizationAdmin") {
      if (page >= 1 && page <= organizationTotalPages) {
        setOrganizationCurrentPage(page);
        getAllAdmin("OrganizationAdmin", page);
      }
    } else if (userType === "SuperAdmin") {
      if (page >= 1 && page <= superAdminTotalPages) {
        setSuperAdminCurrentPage(page);
        getAllAdmin("SuperAdmin", page);
      }
    }
  };

  const getAllAdmin = async (
    type = userType,
    page,
    showLoader = true,
    searchParam
  ) => {
    const search =
      searchParam !== undefined
        ? searchParam
        : type === "IndividualAdmin"
          ? debouncedIndividualSearch
          : type === "OrganizationAdmin"
            ? debouncedOrganizationSearch
            : debouncedSuperAdminSearch;

    const targetPage =
      page ??
      (type === "IndividualAdmin"
        ? individualCurrentPage
        : type === "OrganizationAdmin"
          ? organizationCurrentPage
          : superAdminCurrentPage);

    const cacheKey = adminListCacheKey(targetPage, search);
    const cachedData =
      type === "IndividualAdmin"
        ? individualAdmins[cacheKey]
        : type === "OrganizationAdmin"
          ? organizationAdmins[cacheKey]
          : superAdmins[cacheKey];

    const shouldShowLoader = showLoader && !cachedData;
    if (shouldShowLoader) setIsLoading(true);

    try {
      const response = await SuperAdminService?.getAllAdmin(
        targetPage,
        limit,
        search,
        type
      );
      if (response) {
        dispatch(
          setAdminData({
            type,
            page: targetPage,
            search,
            data: response?.data?.data || [],
            totalPages: Math.ceil(response.data?.totalAdmin / limit),
          })
        );
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    } finally {
      if (shouldShowLoader) setIsLoading(false);
    }
  };

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        getAllAdmin("IndividualAdmin", 1, false, ""),
        getAllAdmin("OrganizationAdmin", 1, false, ""),
        getAllAdmin("SuperAdmin", 1, false, ""),
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAdminData = async (type) => {
    setIsLoading(true);
    try {
      dispatch(clearAdminCache());
      if (type && typeof type === "string") {
        const search =
          type === "IndividualAdmin"
            ? debouncedIndividualSearch
            : type === "OrganizationAdmin"
              ? debouncedOrganizationSearch
              : debouncedSuperAdminSearch;
        const currentPage =
          type === "IndividualAdmin"
            ? individualCurrentPage
            : type === "OrganizationAdmin"
              ? organizationCurrentPage
              : superAdminCurrentPage;
        await getAllAdmin(type, currentPage, false, search);
      } else {
        await Promise.all([
          getAllAdmin("IndividualAdmin", individualCurrentPage, false, debouncedIndividualSearch),
          getAllAdmin("OrganizationAdmin", organizationCurrentPage, false, debouncedOrganizationSearch),
          getAllAdmin("SuperAdmin", superAdminCurrentPage, false, debouncedSuperAdminSearch),
        ]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAdmin = async (userId) => {
    try {
      const response = await SuperAdminService?.deleteAdmin(userId);
      if (response) {
        toastSuccess(response?.data?.message);
        await refreshAdminData();
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    }
  };

  useEffect(() => {
    if (!isLoggedin) {
      skipInitialIndividualSearchFetch.current = true;
      skipInitialOrganizationSearchFetch.current = true;
      skipInitialSuperAdminSearchFetch.current = true;
      return;
    }
    fetchAllData();
    // Only re-run initial load when login state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedin]);

  useEffect(() => {
    if (!isLoggedin) return;
    if (skipInitialIndividualSearchFetch.current) {
      skipInitialIndividualSearchFetch.current = false;
      return;
    }
    setIndividualCurrentPage(1);
    getAllAdmin("IndividualAdmin", 1, true, debouncedIndividualSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedIndividualSearch, isLoggedin]);

  useEffect(() => {
    if (!isLoggedin) return;
    if (skipInitialOrganizationSearchFetch.current) {
      skipInitialOrganizationSearchFetch.current = false;
      return;
    }
    setOrganizationCurrentPage(1);
    getAllAdmin("OrganizationAdmin", 1, true, debouncedOrganizationSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedOrganizationSearch, isLoggedin]);

  useEffect(() => {
    if (!isLoggedin) return;
    if (skipInitialSuperAdminSearchFetch.current) {
      skipInitialSuperAdminSearchFetch.current = false;
      return;
    }
    setSuperAdminCurrentPage(1);
    getAllAdmin("SuperAdmin", 1, true, debouncedSuperAdminSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSuperAdminSearch, isLoggedin]);

  return (
    <ContentLayout>
      <Card className="grid gap-5">
        <div className="flex items-center justify-between">
          <h1 className="font-semibold text-xl">Admin</h1>
          {userType !== "SuperAdmin" && (
            <ModalPopUpCompany
              type="add"
              getAllAdmin={refreshAdminData}
            />
          )}
        </div>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-4">
          <Tabs
            value={userType}
            className="w-full sm:w-auto"
            onValueChange={(value) => {
              setUserType(value);
            }}
          >
            <TabsList className="bg-darkGray text-white flex-wrap h-auto gap-1 py-1">
              <TabsTrigger
                value="IndividualAdmin"
                className="data-[state=active]:bg-primary data-[state=active]:text-white text-white"
              >
                Individual
              </TabsTrigger>
              <TabsTrigger
                value="OrganizationAdmin"
                className="data-[state=active]:bg-primary data-[state=active]:text-white text-white"
              >
                Organization
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative w-full sm:max-w-xs shrink-0">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40 pointer-events-none" />
            <Input
              type="search"
              value={
                userType === "IndividualAdmin"
                  ? individualSearchInput
                  : userType === "OrganizationAdmin"
                    ? organizationSearchInput
                    : superAdminSearchInput
              }
              onChange={(e) => {
                if (userType === "IndividualAdmin") {
                  setIndividualSearchInput(e.target.value);
                } else if (userType === "OrganizationAdmin") {
                  setOrganizationSearchInput(e.target.value);
                } else {
                  setSuperAdminSearchInput(e.target.value);
                }
              }}
              placeholder={
                userType === "IndividualAdmin"
                  ? "Search individuals…"
                  : userType === "OrganizationAdmin"
                    ? "Search organizations…"
                    : "Search super admins…"
              }
              className="pl-9 bg-darkGray border-border text-white placeholder:text-white/40"
            />
          </div>
        </div>
        <div className="!border-border !border rounded-lg relative w-full overflow-auto">
          <Table>
            <TableHeader>
              <TableRow className="!bg-darkGray text-white">
                <TableHead className="w-[100px] text-white">No.</TableHead>
                <TableHead className="text-white">Name</TableHead>
                {userType === "OrganizationAdmin" && (
                  <TableHead className="text-white">Company</TableHead>
                )}
                <TableHead className="text-white">Phone Number</TableHead>
                <TableHead className="text-white">Email</TableHead>
                    {userType === "OrganizationAdmin" && (
                  <TableHead className="text-white">subscription</TableHead>
                )}
                <TableHead className="w-14 text-center text-white">
                  Action
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && currentTableRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={tableColSpan}
                    className="text-center h-40"
                  >
                    <div className="flex justify-center items-center">
                      <img src={Loader} alt="Loader" className="w-20" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : currentTableRows.length > 0 ? (
                currentTableRows.map((adminData, index) => (
                  <TableRow key={adminData._id}>
                    <TableCell>{(currentPageForType - 1) * limit + (index + 1)}</TableCell>
                    <TableCell>{adminData.name}</TableCell>
                    {userType === "OrganizationAdmin" && (
                      <TableCell>{adminData.company?.name}</TableCell>
                    )}
                    <TableCell>{adminData.mobileNumber}</TableCell>
                    <TableCell>{adminData.email}</TableCell>
                        {userType === "OrganizationAdmin" && (
                      <TableCell>
                        <span
                          className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium border ${
                            adminData?.orgSubscription
                              ? "bg-green-500/20 text-green-400 border-green-500/40"
                              : "bg-red-500/20 text-red-400 border-red-500/40"
                          }`}
                        >
                          {adminData?.orgSubscription ? "Active" : "Inactive"}
                        </span>
                      </TableCell>
                    )}
                    <TableCell className="w-14">
                      <div className="flex items-center justify-center gap-2">
                        {userType !== "SuperAdmin" && (
                          <ModalPopUpCompany
                            type="edit"
                            getAllAdmin={refreshAdminData}
                            data={adminData}
                          />
                        )}
                        {/* {userType !== "IndividualAdmin" && ( */}
                        <div
                          className="group border rounded-md p-1 hover:bg-primary border-primary duration-200 cursor-pointer"
                          onClick={() =>
                            navigate("/admin/view-admin", {
                              state: { id: adminData?._id },
                            })
                          }
                        >
                          <Eye className="h-4 w-4 text-primary group-hover:text-white" />
                        </div>
                        {/* )} */}

                        {/* <div className="border rounded-md p-1 hover:bg-[#7064E9] hover:border-[#7064E9] duration-200 cursor-pointer">
                      <Pencil className="h-4 w-4" />
                    </div> */}
                        {userType !== "SuperAdmin" && (
                          <DeleteModal
                            handleDelete={() => deleteAdmin(adminData?._id)}
                          />
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={tableColSpan}
                    className="text-center h-20 font-bold"
                  >
                    Admin Not Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {currentTableRows.length > 0 && (
            <div className="flex justify-end items-center">
              <Pagination
                currentPage={currentPageForType}
                totalPages={totalPagesForType}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
      </Card>
    </ContentLayout>
  );
};

export default Admin;
